import React, { Component } from 'react'
import ReactTooltip from 'react-tooltip'
import './ScoreCards.css'

import localize from '../localize';
import ScoreNumber from '../ScoreNumber';
import Assessment from '../Assessment';
import { scoreBracket } from '../../utils/score_meta';
import { sortIsps } from '../../store/reducers/utils';
import getIspData from '../../utils/get_isp_data';
import showNotification from '../../utils/show_notification';

const getBestIsp = (player, timeWindow) => {
  if (player.location.isps.length > 0) {
    return sortIsps(player.location.isps, timeWindow, 'connection_score')[0];
  } else {
    return false;
  }
}

const SideBlock = ({ children, topAlign }) => (
  <div className={`side-component${topAlign? ' error-text-block' : ''}`}>
    { children }
  </div>
)

const CenterBlock = ({ children, topAlign }) => (
  <div className={`center-component${topAlign? ' error-text-block' : ''}`}>
    { children }
  </div>
)

const ScoreHeader = ({ label, isp }) => (
  <div className="scorecard-header">
    <div className="scorecard-label">{label}</div>
    <div className="scorecard-isp">{isp}</div>
  </div>
)

const SideHeader = ({ label, isp }) => (
  <SideBlock>
    <ScoreHeader label={label} isp={isp} />
  </SideBlock>
)

const CenterHeader = ({ label, isp }) => (
  <CenterBlock>
    <ScoreHeader label={label} isp={isp} />
  </CenterBlock>
)

const ScoreLabel = localize('pages.scores')(({ i18n, bracket, children }) => (
  <div className="score-label">
    <span dangerouslySetInnerHTML={{ __html: i18n.t(`quality.${bracket}`).replace('<span', `<span class="score-label-${bracket}"`) }} />
  { children }
  </div>
))

const MyIspScore = ({ number, glow }) => (
  <SideBlock>
    <ScoreNumber glow={glow} value={number} size="lg" />
    <ScoreLabel bracket={scoreBracket(number)} />
    <div className="bottom-bar" />
  </SideBlock>
)

const MyScore = ({ number, glow, open, close, assessmentData, isOpen }) => (
  <CenterBlock>
    <ScoreNumber glow={glow} onClick={showNotification(assessmentData)? open : () => {}} active={showNotification(assessmentData)} value={number} size="xl" />
    <strong><ScoreLabel bracket={scoreBracket(number)}>{ showNotification(assessmentData) && <Assessment open={open} close={close} data={assessmentData} isOpen={isOpen} /> }</ScoreLabel></strong>
    <div className="bottom-bar" />
  </CenterBlock>
)

const BestIspScore = ({ number, glow }) => (
  <SideBlock>
    <ScoreNumber glow={glow} value={number} size="lg" />
    <ScoreLabel bracket={scoreBracket(number)} />
    <div className="bottom-bar" />
  </SideBlock>
)

export const KeyValue = ({ k, v }) => (
  <div className="key-value-container">
    <div className="data-table-key">{k}</div>
    <div className="data-table-value">{v}</div>
  </div>
)

export const MetricTooltip = localize()(({ i18n, metric }) => (
  <ReactTooltip id={metric} border={true} delayShow={300}>
    <table className="metric-tooltip">
      <tbody>
        <tr>
          <th>
            {i18n.t(`pages.scores.help.metrics.${metric}.header`)}
          </th>
        </tr>
        <tr>
          <td>
            {i18n.t(`pages.scores.help.metrics.${metric}.body`)}
          </td>
        </tr>
      </tbody>
    </table>
  </ReactTooltip>
))

export const IspDataTable = localize()(({ i18n, stats }) => (
  <div className="side-data-table">
    <table>
      <tbody>
        <tr>
          <th className="table-key"><strong>{i18n.t('metrics.ethernet')}</strong></th>
          <th className="table-value"><strong>{stats.connection_score}</strong></th>
        </tr>
        <tr>
          <td className="table-key">
            <span className="glow-key" data-tip data-for="latency">{i18n.t('metrics.latency')}</span>
            <MetricTooltip metric="latency"/>
          </td>
          <td className="table-value">{i18n.ms(stats.latency)}</td>
        </tr>
        <tr>
          <td className="table-key">
            <span className="glow-key" data-tip data-for="packet_loss">{i18n.t('metrics.packet_loss')}</span>
            <MetricTooltip metric="packet_loss"/>
          </td>
          <td className="table-value">{i18n.fractionalPercent(stats.packet_loss)}</td>
        </tr>
        <tr>
          <td className="table-key">
            <span className="glow-key" data-tip data-for="jitter">{i18n.t('metrics.jitter')}</span>
            <MetricTooltip metric="jitter"/>
          </td>
          <td className="table-value">{i18n.ms(stats.jitter)}</td>
        </tr>
      </tbody>
    </table>
  </div>
))

const SideDataTable = localize()(({ i18n, stats }) => (
  <SideBlock>
    <IspDataTable stats={stats} />
  </SideBlock>
))

const CenterDataTable = localize()(({ i18n, statGroup }) => {
  var wirelessPart
  if (statGroup.stats_wireless) {
    wirelessPart = (
      <div className="center-data-table">
        <table>
          <tbody>
            <tr>
              <th className="table-key"><strong>{i18n.t('metrics.wifi')}</strong></th>
              <th className="table-value"><strong>{statGroup.stats_wireless.connection_score}</strong></th>
            </tr>
            <tr>
              <td className="table-key">
                <span className="glow-key" data-tip data-for="latency">{i18n.t('metrics.latency')}</span>
                <MetricTooltip metric="latency"/>
              </td>
              <td className="table-value">{i18n.ms(statGroup.stats_wireless.latency)}</td>
            </tr>
            <tr>
              <td className="table-key">
                <span className="glow-key" data-tip data-for="packet_loss">{i18n.t('metrics.packet_loss')}</span>
                <MetricTooltip metric="packet_loss"/>
              </td>
              <td className="table-value">{i18n.fractionalPercent(statGroup.stats_wireless.packet_loss)}</td>
            </tr>
            <tr>
              <td className="table-key">
                <span className="glow-key" data-tip data-for="jitter">{i18n.t('metrics.jitter')}</span>
                <MetricTooltip metric="jitter"/>
              </td>
              <td className="table-value">{i18n.ms(statGroup.stats_wireless.jitter)}</td>
            </tr>
            <tr>
              <td className="table-key">{i18n.t('metrics.player_games')}</td>
              <td className="table-value">{statGroup.stats_wireless.player_games}</td>
            </tr>
          </tbody>
        </table>
      </div>
    )
  } else {
    wirelessPart = (
      <div className="center-data-table">
        <table>
          <tbody>
            <tr>
              <th className="table-key"><strong>{i18n.t('metrics.wifi')}</strong></th>
              <th className="table-value"><strong>--</strong></th>
            </tr>
          </tbody>
        </table>
        <div className="no-games">{i18n.t('pages.scores.cards.errors.breakdown.no_wifi')}</div>
      </div>
    )
  }

  var ethernetPart
  if (statGroup.stats_ethernet) {
    ethernetPart = (
      <div className="center-data-table">
        <table>
          <tbody>
            <tr>
              <th className="table-key"><strong>{i18n.t('metrics.ethernet')}</strong></th>
              <th className="table-value"><strong>{statGroup.stats_ethernet.connection_score}</strong></th>
            </tr>
            <tr>
              <td className="table-key">
                <span className="glow-key" data-tip data-for="latency">{i18n.t('metrics.latency')}</span>
                <MetricTooltip metric="latency"/>
              </td>
              <td className="table-value">{i18n.ms(statGroup.stats_ethernet.latency)}</td>
            </tr>
            <tr>
              <td className="table-key">
                <span className="glow-key" data-tip data-for="packet_loss">{i18n.t('metrics.packet_loss')}</span>
                <MetricTooltip metric="packet_loss"/>
              </td>
              <td className="table-value">{i18n.fractionalPercent(statGroup.stats_ethernet.packet_loss)}</td>
            </tr>
            <tr>
              <td className="table-key">
                <span className="glow-key" data-tip data-for="jitter">{i18n.t('metrics.jitter')}</span>
                <MetricTooltip metric="jitter"/>
              </td>
              <td className="table-value">{i18n.ms(statGroup.stats_ethernet.jitter)}</td>
            </tr>
            <tr>
              <td className="table-key">{i18n.t('metrics.player_games')}</td>
              <td className="table-value">{statGroup.stats_ethernet.player_games}</td>
            </tr>
          </tbody>
        </table>
      </div>
    )
  } else {
    ethernetPart = (
      <div className="center-data-table">
        <table>
          <tbody>
            <tr>
              <th className="table-key"><strong>{i18n.t('metrics.ethernet')}</strong></th>
              <th className="table-value"><strong>--</strong></th>
            </tr>
          </tbody>
        </table>
        <div className="no-games">{i18n.t('pages.scores.cards.errors.breakdown.no_ethernet')}</div>
      </div>
    )
  }

  return (
    <CenterBlock>
      <div className="left-section">
        { ethernetPart }
      </div>
      <div className="right-section">
        { wirelessPart }
      </div>
    </CenterBlock>
  )
})

const ErrorText = localize('pages.scores.cards')(({ i18n, cardType, errorType }) => (
  <div className="error-text-container">
    <div className="error-text" dangerouslySetInnerHTML={{__html: i18n.t(`errors.${cardType}.${errorType}.message`)}} />
  </div>
))

const NotLoggedIn = localize('pages.scores.cards.errors.yourscore.login')(({ i18n }) => {
  const handleLogin = () => {
    const RiotBar = window.RiotBar;
    RiotBar.account.login();
  }

  return (
    <CenterBlock topAlign={true}>
      <ErrorText cardType="yourscore" errorType="login" />
      <q-button onClick={handleLogin}>{i18n.t('button')}</q-button>
    </CenterBlock>
  )
})

const ErrorBlock = ({ cardType, errorType }) => (
  <SideBlock topAlign={true}>
    <ErrorText cardType={cardType} errorType={errorType} />
  </SideBlock>
)

const CenterErrorBlock = ({ cardType, errorType }) => (
  <CenterBlock topAlign={true}>
    <div className="error-extra-padding">
      <ErrorText cardType={cardType} errorType={errorType} />
    </div>
  </CenterBlock>
)

class ScoreCards extends Component {
  constructor(props) {
    super(props)

    this.state = {
      new: true
    }
  }

  stopGlow() {
    this.setState({ new: false })
  }

  componentDidMount() {
    window.setTimeout(this.stopGlow.bind(this), 300)
  }

  componentDidUpdate(lastProps, lastState) {
    if (lastProps !== this.props) {
      this.setState({new: true})
    } else if (this.state.new) {
      window.setTimeout(this.stopGlow.bind(this), 300)
    }
  }

  render() {
    const { i18n, player, timeWindow, openAssessment, closeAssessment } = this.props

    var showPlayerData = false
    var showPlayerIspData = false
    const bestIsp = getBestIsp(player, timeWindow)

    const assessmentData = {
      player: player && player.stats? getIspData(player.stats, timeWindow) : false,
      isp: player && player.isp? getIspData(player.isp.stats, timeWindow) : false,
      bestIsp: bestIsp? getIspData(bestIsp.stats, timeWindow) : false
    }

    var playerSection
    if (!player.isLoggedIn) {
      playerSection = <NotLoggedIn />
    } else if (player.not_enough_data || !player.isp || !getIspData(player.stats, timeWindow)) {
      playerSection = <CenterErrorBlock cardType="yourscore" errorType="not_enough_data" />
    } else {
      playerSection = <MyScore glow={this.state.new} number={getIspData(player.stats, timeWindow).stats_overall.connection_score} open={openAssessment} close={closeAssessment} assessmentData={assessmentData} isOpen={player.assessmentOpen} />
      showPlayerData = true
    }

    var playerIspSection
    if (player.isp && player.isp.is_private) {
      playerIspSection = <ErrorBlock cardType="yourisp" errorType="private" />
    } else if (!player.isp || player.isp.not_enough_data) {
      playerIspSection = <ErrorBlock cardType="yourisp" errorType="not_enough_data" />
    } else {
      playerIspSection = <MyIspScore glow={this.state.new} number={getIspData(player.isp.stats, timeWindow).stats_ethernet.connection_score} />
      showPlayerIspData = true
    }

    return (
      <div className="scorecards">
        <div className="three-col-container">
          <SideHeader label={i18n.t('cards.yourisp.header')} isp={player.isp.name} />
          <CenterHeader label={i18n.t('cards.yourscore.header')} isp={player.isp.name} />
          <SideHeader label={i18n.t('cards.highest.header')} isp={getBestIsp(player, timeWindow).name} />
        </div>
        <div className="three-col-container">
          { playerIspSection }
          { playerSection }
          { bestIsp? <BestIspScore glow={this.state.new} number={getIspData(bestIsp.stats, timeWindow).stats_ethernet.connection_score} /> : <ErrorBlock cardType="highest" errorType="not_enough_data" />}
          { showPlayerIspData? <SideDataTable stats={getIspData(player.isp.stats, timeWindow).stats_ethernet} /> : <SideBlock />}
          { showPlayerData? <CenterDataTable statGroup={getIspData(player.stats, timeWindow)} /> : <CenterBlock />}
          { bestIsp? <SideDataTable stats={getIspData(bestIsp.stats, timeWindow).stats_ethernet} /> : <SideBlock />}
        </div>
      </div>
    )
  }
}

export default localize()(ScoreCards)



// WEBPACK FOOTER //
// ./src/components/ScoreCards/index.js